import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Map, List, is } from 'immutable';
import { connect } from 'react-redux';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import Popups from '../../components/Popup';
import config from '../../config';
import {
  saveProjectDetails,
  loadProject,
  reset,
  updateProjectInfo,
  selectProjectSuggestion,
} from '../../actions/company/buyer/addOnProject/main';
import {
  loadUsers,
  deleteUserRow,
  loadUsersSuggestion,
  selectUserSuggestion,
  setSuggestion,
} from '../../actions/company/buyer/addOnProject/suggestion';
import { fetchProjectTags, updateProjectTags } from '../../actions/company/buyer/addOnProject/projectTags';
import { openInternalLink } from '../../utils/url';
import { showError } from '../../utils/MessagePopup';
import { getIn, getOriginalIn, isDeepChanged, unwrap } from '../../utils/ChangeSpy';
import deletionCheck from '../../utils/deletingPermission';
import { BUSINESS_MODELS, INDUSTRY_CATEGORIES, INDUSTRY_TAGS_ERROR } from '../../utils/industryTagsHelper';
import { usePopupContext } from '../../contexts/PopupContext';

const types = config.values.getIn(['project', 'types']);
const NEW_ROW_ID = -1;

const getSelectedIds = list =>
  list.reduce((accumulator, item) => {
    accumulator[item.get('id')] = true;

    return accumulator;
  }, {});

/**
 * Container of the add-on project details popup on the buyer page. It renders the same popup as the project page and
 * adapts the add-on store (users and industry tags kept in ChangeSpy) to its rows; the edited user row lives in local
 * state. Closing this popup discards unsaved changes by reloading the edited data, then calls `onClose` from popup props.
 */
const AddOnProjectDetailsPopupContainer = props => {
  const {
    common,
    customProjectId,
    deleteUserRow,
    fetchProjectTags,
    isUserSuggestionsLoading,
    loadProject,
    loadUsers,
    loadUsersSuggestion,
    onClose,
    projectData,
    reset,
    saveProjectDetails,
    selectProjectSuggestion,
    selectUserSuggestion,
    setSuggestion,
    tags,
    updateProjectInfo,
    updateProjectTags,
    user,
    userSuggestionList,
    users,
  } = props;

  const { openPopup, closePopup, onClosePopup } = usePopupContext();
  const [isSaving, setIsSaving] = useState(false);
  const [editedUser, setEditedUser] = useState(null);

  const projectId = projectData.get('id');

  const reopenPopup = useCallback(() => {
    openPopup('AddOnProjectDetailsPopup', { customProjectId, onClose });
  }, [openPopup, customProjectId, onClose]);

  const openChildPopup = useCallback(
    (name, popupProps) => {
      openPopup(name, popupProps);
      onClosePopup(reopenPopup);
    },
    [openPopup, onClosePopup, reopenPopup],
  );

  const handleDiscard = useCallback(() => {
    reset();
    Promise.all([loadProject(projectId), loadUsers(projectId), fetchProjectTags(projectId)]).then(() => {
      if (onClose) {
        onClose();
      }
    });
  }, [reset, loadProject, loadUsers, fetchProjectTags, projectId, onClose]);

  useEffect(() => {
    onClosePopup(handleDiscard);
  }, [onClosePopup, handleDiscard]);

  const handleSave = useCallback(() => {
    const categories = unwrap(tags.get(INDUSTRY_CATEGORIES));
    const models = unwrap(tags.get(BUSINESS_MODELS));

    if (!categories.size || !models.size) {
      showError(openChildPopup, [INDUSTRY_TAGS_ERROR], reopenPopup);

      return;
    }

    setIsSaving(true);
    saveProjectDetails().then(isSaved => {
      setIsSaving(false);
      if (isSaved) {
        closePopup();
      }
    });
  }, [tags, openChildPopup, reopenPopup, saveProjectDetails, closePopup]);

  const handleTextChange = useCallback(
    ({ event, data: { type } }) => {
      const {
        target: { name, value, checked },
      } = event;

      if (type === types.get('USERS')) {
        const isSuggesting = value.length >= config.NUMBER_OF_MIN_CHARACTER;

        setEditedUser(previous => ({ ...previous, text: value, isSuggesting }));
        if (isSuggesting) {
          loadUsersSuggestion(value);
        }
      } else if (type === types.get('PROJECT')) {
        updateProjectInfo(name, event.target.type.toUpperCase() === 'CHECKBOX' ? checked : value, customProjectId);
      }
    },
    [loadUsersSuggestion, updateProjectInfo, customProjectId],
  );

  const handleFocus = useCallback(
    (name, value = '') => {
      updateProjectInfo(name, value, customProjectId);
    },
    [updateProjectInfo, customProjectId],
  );

  const handleSuggestionClose = useCallback(
    ({ data }) => {
      if (data.type === types.get('USERS')) {
        setEditedUser(previous => previous && { ...previous, isSuggesting: false });
      } else {
        reset();
      }
    },
    [reset],
  );

  const handleSuggestionSelect = useCallback(
    ({ data, suggestion }) => {
      if (data.type === types.get('USERS')) {
        selectUserSuggestion({ rowIndex: editedUser.index, id: suggestion.get('id'), text: suggestion.get('name') });
        setEditedUser(null);
        setSuggestion({ path: 'user.list', value: List() });
      } else {
        selectProjectSuggestion(data.name, suggestion.get('id'), suggestion.get('name'));
      }
    },
    [editedUser, selectUserSuggestion, setSuggestion, selectProjectSuggestion],
  );

  const handleSuggestionInputClick = useCallback(
    ({ event }) => {
      event.stopPropagation();
      reset();
    },
    [reset],
  );

  const handleRowClick = useCallback(
    ({ row, type }) => {
      if (type === types.get('USERS')) {
        const index = users.findIndex(userRow => getIn(userRow, 'id') === row.get('id'));

        setEditedUser({ index, text: row.get('name'), isSuggesting: false });
      }
    },
    [users],
  );

  const handleRowDoubleClick = useCallback(({ row, type }) => {
    if (type === types.get('USERS')) {
      openInternalLink(`/user/${row.get('id')}`);
    }
  }, []);

  const handleRowInsert = useCallback(
    type => {
      if (type === types.get('USERS')) {
        setEditedUser({ index: users.size, text: '', isSuggesting: false });
      } else if (type === types.get('INDUSTRIES')) {
        openChildPopup('TagsManagementPopup', {
          addNew: false,
          clientView: true,
          selectedTags: getSelectedIds(unwrap(tags.get(INDUSTRY_CATEGORIES))),
          selectedModels: getSelectedIds(unwrap(tags.get(BUSINESS_MODELS))),
          callback: updateProjectTags,
        });
      }
    },
    [users, openChildPopup, tags, updateProjectTags],
  );

  const deleteRowAfterConfirm = useCallback(
    (event, { id, type }) => {
      if (type === types.get('USERS')) {
        deleteUserRow(id);
      }
    },
    [deleteUserRow],
  );

  const handleDeleteRow = useMemo(() => deletionCheck(user, deleteRowAfterConfirm, { openPopup: openChildPopup }), [
    user,
    deleteRowAfterConfirm,
    openChildPopup,
  ]);

  const handleReset = useCallback(() => {
    reset();
    setEditedUser(null);
  }, [reset]);

  const industries = useMemo(
    () =>
      unwrap(tags.get(INDUSTRY_CATEGORIES))
        .concat(unwrap(tags.get(BUSINESS_MODELS)))
        .map(tag => Map({ id: tag.get('id'), name: tag.get('category') })),
    [tags],
  );

  const isIndustriesChanged =
    !is(getIn(tags, INDUSTRY_CATEGORIES), getOriginalIn(tags, INDUSTRY_CATEGORIES)) ||
    !is(getIn(tags, BUSINESS_MODELS), getOriginalIn(tags, BUSINESS_MODELS));

  const userRows = useMemo(() => {
    const rows = users.map(userRow =>
      Map({
        id: getIn(userRow, 'id'),
        name: getIn(userRow, 'text'),
        dirty: isDeepChanged(userRow),
        mode: config.VIEW_MODE,
      }),
    );

    if (!editedUser) {
      return rows;
    }

    let mode = config.EDIT_MODE;

    if (editedUser.isSuggesting) {
      mode = isUserSuggestionsLoading ? config.LOADING_MODE : config.SUGGEST_MODE;
    }

    const editedRow = Map({ id: rows.getIn([editedUser.index, 'id'], NEW_ROW_ID), name: editedUser.text, mode });

    return rows.set(editedUser.index, editedRow);
  }, [users, editedUser, isUserSuggestionsLoading]);

  const userSuggestions = useMemo(
    () => userSuggestionList.map(suggestion => Map({ id: suggestion.id, name: suggestion.text })),
    [userSuggestionList],
  );

  return (
    <Popups.ProjectDetailsPopup
      common={common}
      industries={industries}
      isIndustriesChanged={isIndustriesChanged}
      isSaving={isSaving}
      onCancel={closePopup}
      onDeleteRow={handleDeleteRow}
      onFocus={handleFocus}
      onReset={handleReset}
      onRowClick={handleRowClick}
      onRowDoubleClick={handleRowDoubleClick}
      onRowInsert={handleRowInsert}
      onSave={handleSave}
      onSuggestionClose={handleSuggestionClose}
      onSuggestionInputClick={handleSuggestionInputClick}
      onSuggestionSelect={handleSuggestionSelect}
      onTextChange={handleTextChange}
      projectData={projectData}
      users={userRows}
      userSuggestions={userSuggestions}
      isBuyerHidden
    />
  );
};

AddOnProjectDetailsPopupContainer.propTypes = {
  common: PropTypes.instanceOf(Map).isRequired,
  customProjectId: PropTypes.string,
  isUserSuggestionsLoading: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  projectData: PropTypes.instanceOf(Map).isRequired,
  tags: PropTypes.instanceOf(Map).isRequired,
  user: PropTypes.instanceOf(Map),
  users: PropTypes.instanceOf(List).isRequired,
  userSuggestionList: PropTypes.instanceOf(List).isRequired,
};

const mapStateToProps = (state, props) => {
  const { common, projectData, suggestions, tags, users } = state.targetCompany.buyer.addOnProject;

  return {
    common,
    customProjectId: props.popup.getIn(['props', 'customProjectId']),
    onClose: props.popup.getIn(['props', 'onClose']),
    isUserSuggestionsLoading: suggestions.getIn(['user', 'loading']),
    projectData,
    tags,
    user: state.auth.get('user', null),
    userSuggestionList: suggestions.getIn(['user', 'list']),
    users,
  };
};

export { AddOnProjectDetailsPopupContainer };
export default connect(
  mapStateToProps,
  {
    saveProjectDetails,
    loadProject,
    reset,
    updateProjectInfo,
    selectProjectSuggestion,
    loadUsers,
    deleteUserRow,
    loadUsersSuggestion,
    selectUserSuggestion,
    setSuggestion,
    fetchProjectTags,
    updateProjectTags,
  },
  mergeProps,
  connectOptions,
)(memo(AddOnProjectDetailsPopupContainer));
